import { useState, useMemo } from 'react';
import type { PartnerConfig, ChatState, CustomerInfo, AttachedFile } from '../types';
import type { PendingFile } from './FilePreview';
import { Header } from './Header';
import { DropOverlay } from './DropOverlay';
import { TabBar, TabContent, ChatView, FilesView, YouView, type TabType } from './';

interface LanguageState {
  current: string;
  cf_language: string | null;
  show_cf_suggestion: boolean;
}

interface ChatWindowProps {
  config: PartnerConfig;
  onSendMessage: (message: string) => void;
  state: ChatState;
  onCustomerSubmit: (customer: Partial<CustomerInfo>) => void;
  onDrawerDismiss: () => void;
  onDrawerOpen: () => void;
  pendingFiles: PendingFile[];
  onFilesSelected: (files: File[]) => void;
  onFileRemove: (id: string) => void;
  // Feature flags
  fileUploadEnabled?: boolean;
  researchModeEnabled?: boolean;
  // Language props
  languageState?: LanguageState;
  onSelectLanguage?: (code: string) => void;
  onDismissLanguageSuggestion?: () => void;
  // Research mode props
  onVerifyEmail?: () => void;
  onConfirmOtp?: (code: string) => void;
  onResendOtp?: () => void;
  onToggleResearchMode?: (enabled: boolean) => void;
  onUpdateNotificationPrefs?: (prefs: { browser: boolean; email: boolean }) => void;
}

export function ChatWindow({
  config,
  onSendMessage,
  state,
  onCustomerSubmit,
  pendingFiles,
  onFilesSelected,
  onFileRemove,
  fileUploadEnabled = true,
  researchModeEnabled = true,
  languageState,
  onSelectLanguage,
  onDismissLanguageSuggestion,
  onVerifyEmail,
  onConfirmOtp,
  onResendOtp,
  onToggleResearchMode,
  onUpdateNotificationPrefs,
}: ChatWindowProps) {
  const [activeTab, setActiveTab] = useState<TabType>('chat');

  const supportedLanguages = config.supported_languages || [];

  // Extract files from sent messages (user messages with files attached)
  const sentFiles = useMemo<AttachedFile[]>(() => {
    return state.messages
      .filter((m) => m.role === 'user' && m.files && m.files.length > 0)
      .flatMap((m) => m.files || []);
  }, [state.messages]);

  // Badge states for tabs - show count of pending + sent files
  const totalFiles = pendingFiles.length + sentFiles.length;
  const badges = {
    files: totalFiles,
    youAction: (state.drawerTriggerIntent !== null || state.researchMode.verificationPending) && activeTab !== 'you',
  };

  return (
    <DropOverlay
      onFilesSelected={onFilesSelected}
      disabled={!state.isConnected}
    >
      <div
        className="flex flex-col h-full bg-white rounded-lg shadow-lg overflow-hidden relative"
        style={{
          '--color-primary-override': config.branding.colors.primary,
          '--color-secondary-override': config.branding.colors.secondary,
          '--color-background-override': config.branding.colors.background,
          '--color-text-override': config.branding.colors.text,
        } as React.CSSProperties}
      >
        <Header config={config} />

        <TabBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          badges={badges}
          // Feature flags to hide tabs
          hideFilesTab={!fileUploadEnabled}
          // Language tab props
          supportedLanguages={supportedLanguages}
          currentLanguage={languageState?.current || 'en'}
          onSelectLanguage={onSelectLanguage || (() => {})}
          // CF suggestion props
          cfLanguage={languageState?.cf_language}
          showCfSuggestion={languageState?.show_cf_suggestion}
          onDismissLanguageSuggestion={onDismissLanguageSuggestion}
        />

        <TabContent
          activeTab={activeTab}
          chatView={
            <ChatView
              messages={state.messages}
              isTyping={state.isTyping}
              statusMessage={state.statusMessage}
              onSend={onSendMessage}
              isConnected={state.isConnected}
              pendingFiles={pendingFiles}
              onFileRemove={onFileRemove}
              onFilesSelected={onFilesSelected}
              onTabChange={setActiveTab}
              streamingMessage={state.streamingMessage}
            />
          }
          filesView={
            <FilesView
              pendingFiles={pendingFiles}
              sentFiles={sentFiles}
              onRemove={onFileRemove}
              onFilesSelected={onFilesSelected}
              disabled={!state.isConnected}
            />
          }
          youView={
            <YouView
              countries={config.contact_form.countries}
              defaultCountry={config.contact_form.default_country}
              existingCustomer={state.customer}
              privacyPolicyUrl={config.privacy_policy_url}
              onCustomerSubmit={onCustomerSubmit}
              // Email verification props
              emailVerified={state.researchMode.verified}
              verificationPending={state.researchMode.verificationPending}
              onVerifyEmail={onVerifyEmail || (() => {})}
              onConfirmOtp={onConfirmOtp || (() => {})}
              onResendOtp={onResendOtp || (() => {})}
              // Research mode props
              showResearchMode={researchModeEnabled}
              researchModeState={state.researchMode}
              onToggleResearchMode={onToggleResearchMode || (() => {})}
              onUpdateNotificationPrefs={onUpdateNotificationPrefs || (() => {})}
            />
          }
        />

        {config.branding.show_powered_by && (
          <div className="text-center text-xs text-gray-400 py-2 border-t">
            Powered by HW group
          </div>
        )}
      </div>
    </DropOverlay>
  );
}
